import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { getRelayOutputCount, isAncFamilyDevice } from 'src/app/services/devices/device-type.util';
import { AncDataService } from 'src/app/services/ANC/anc-data.service';
import { DeviceService } from 'src/app/services/devices/device.service';
import { DlDataService } from 'src/app/services/DL/dl-data.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { ModalAncSuntimesYearlyComponent } from 'src/app/components/ui/modal-anc-suntimes-yearly/modal-anc-suntimes-yearly.component';

@Component({
    selector: 'app-anc-device-settings',
    imports: [CommonModule, FormsModule, TranslateModule],
    templateUrl: './anc-device-settings.component.html'
})
export class AncDeviceSettingsComponent implements OnInit {

  @Input()
  allSettings = false;
  device: string = '';
  outputCount = 2;
  successMessage: boolean = false;
  error: string = '';
  originalDataDevice: any;
  originalDataCalendar: any;
  changesMadeDevice = false;
  changesMadeCalendar = false;
  dataCfgDevice: any = {};
  dataCfgCalendar: any = {
    mode1224: 0,
    corrSunset: 0,
    corrSunrise: 0
  };
  modelData: { description?: string; languages: string[]; countries?: any[] } = {
    languages: [],
    countries: [],
  };
  isAncFamily = false;
  sunset: any;
  sunrise: any;
  signoCorrSunrise: any;
  signoCorrSunset: any;

  constructor(
    private _aRoute: ActivatedRoute,
    public _deviceService: DeviceService,
    private _ancDataService: AncDataService,
    private _utilsService: UtilsService,
    private _dlDataService: DlDataService,
    private _modal: ModalService
  ) {}

  ngOnInit(): void {
    this._aRoute.parent?.parent?.params.subscribe((params: Params) => {
      this.device = params['serial'];
      this.outputCount = getRelayOutputCount(this.device);
    });

    this._deviceService.getDeviceInfo(this.device).subscribe((deviceInfo) => {
      this.isAncFamily = isAncFamilyDevice(deviceInfo);
      this.loadDeviceSettings();
    });

    this._deviceService.getModuleInfo(this.device, 'calendar').subscribe((data) => {
      this.originalDataCalendar = data.cfg;
      this.dataCfgCalendar = JSON.parse(JSON.stringify(this.originalDataCalendar));

      if (this.dataCfgCalendar?.mode1224 === undefined || this.dataCfgCalendar?.mode1224 === null) {
        this.dataCfgCalendar.mode1224 = 1;
      }

      this.syncCalendarCorrectionFields();
    });
  }

  private loadDeviceSettings(): void {
    this._deviceService.getModuleInfo(this.device, 'device').subscribe((data) => {
      this.originalDataDevice = { ...data.cfg };
      this.dataCfgDevice = JSON.parse(JSON.stringify(this.originalDataDevice));

      const resolvedModel = this.isAncFamily
        ? this._ancDataService.getModelData(data.cfg?.model)
        : this._dlDataService.getModelData(data.cfg?.model);

      this.modelData = resolvedModel || { languages: [], countries: [] };
    });
  }

  changePermanentOutput(output: number, permanent: boolean) {
    if (this.outputCount < 2) {
      this.dataCfgDevice.permanent = output === 1 && permanent ? 1 : 0;
      this.changesMadeDevice = true;
      return;
    }

    switch (this.dataCfgDevice.permanent) {
      case 1:
        if (output === 1 && !permanent) {
          this.dataCfgDevice.permanent = 0;
        }
        if (output === 2 && permanent) {
          this.dataCfgDevice.permanent = 3;
        }
        break;
      case 2:
        if (output === 2 && !permanent) {
          this.dataCfgDevice.permanent = 0;
        }
        if (output === 1 && permanent) {
          this.dataCfgDevice.permanent = 3;
        }
        break;
      case 3:
        if (output === 1 && !permanent) {
          this.dataCfgDevice.permanent = 2;
        }
        if (output === 2 && !permanent) {
          this.dataCfgDevice.permanent = 1;
        }
        break;
      case 0:
        if (output === 1 && permanent) {
          this.dataCfgDevice.permanent = 1;
        }
        if (output === 2 && permanent) {
          this.dataCfgDevice.permanent = 2;
        }
        break;
      default:
        break;
    }
    this.changesMadeDevice = true;
  }

  save() {
    if (this.changesMadeDevice) {
      if (this.outputCount < 2) {
        const permanent = Number(this.dataCfgDevice.permanent);
        this.dataCfgDevice.permanent = permanent === 1 || permanent === 3 ? 1 : 0;
      }

      const body: Record<string, unknown> = {
        language: this.dataCfgDevice.language,
        contrast: this.dataCfgDevice.contrast,
        pwdEnDis: this.dataCfgDevice.pwdEnDis,
      };

      if (this.isAncFamily) {
        body.country = this.dataCfgDevice.country;
        body.city = this.dataCfgDevice.city;
        body.zoneEnabled = this.dataCfgDevice.zoneEnabled;
      }

      this._deviceService.setModuleCfg(this.device, body, 'device').subscribe(() => {
        this.originalDataDevice = JSON.parse(JSON.stringify(this.dataCfgDevice));
        this.changesMadeDevice = false;
        this.successMessage = true;
        setTimeout(() => {
          this.successMessage = false;
        }, 3000);
      });
    }

    if (this.changesMadeCalendar) {
      const body: Record<string, unknown> = {
        mode1224: this.dataCfgCalendar.mode1224,
      };

      if (this.isAncFamily) {
        body.corrSunrise = this.timeToMinutes(this.sunrise) * (this.signoCorrSunrise === '+' ? 1 : -1);
        body.corrSunset = this.timeToMinutes(this.sunset) * (this.signoCorrSunset === '+' ? 1 : -1);
      }

      this._deviceService.setModuleCfg(this.device, body, 'calendar').subscribe(() => {
        if (this.isAncFamily) {
          this.dataCfgCalendar.corrSunrise = body.corrSunrise as number;
          this.dataCfgCalendar.corrSunset = body.corrSunset as number;
        }
        this.originalDataCalendar = JSON.parse(JSON.stringify(this.dataCfgCalendar));
        this.changesMadeCalendar = false;
        this.successMessage = true;
        setTimeout(() => {
          this.successMessage = false;
        }, 3000);
      });
    }
  }

  private syncCalendarCorrectionFields(): void {
    if (!this.dataCfgCalendar) return;

    this.signoCorrSunrise = this.dataCfgCalendar.corrSunrise >= 0 ? '+' : '-';
    this.signoCorrSunset = this.dataCfgCalendar.corrSunset >= 0 ? '+' : '-';
    this.sunrise = this._utilsService.getDateFromMinutes(Math.abs(this.dataCfgCalendar.corrSunrise));
    this.sunset = this._utilsService.getDateFromMinutes(Math.abs(this.dataCfgCalendar.corrSunset));
  }

  restore() {
    if (this.originalDataDevice) {
      this.dataCfgDevice = JSON.parse(JSON.stringify(this.originalDataDevice));
    }
    if (this.originalDataCalendar) {
      this.dataCfgCalendar = JSON.parse(JSON.stringify(this.originalDataCalendar));
      this.syncCalendarCorrectionFields();
    }
    this.changesMadeDevice = false;
    this.changesMadeCalendar = false;
  }

  timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  openYearlySunTimes(): void {
    const modalRef = this._modal.open(ModalAncSuntimesYearlyComponent, {
      centered: true,
      scrollable: true,
      size: 'xl',
    });
    modalRef.componentInstance.serial = this.device;
  }
}
